/**
 * MoneyLayerCard — surfaces the Wave 3 Money Layer on DegreeDetail:
 *   1. Monthly cost-of-living for the campus city (rent, groceries,
 *      transport, utilities, total, w/ Numbeo source line).
 *   2. Relevant scholarships filtered by destination country + level.
 *
 * Silent when neither dataset has anything for this course (e.g. an
 * obscure city we haven't priced yet). Each row cites its source URL
 * so families can audit — same methodology-transparency posture we
 * use for LEO + Destinations.
 *
 * Added 2026-02-05 as part of the Wave 3 Money Layer.
 */
import { useEffect, useState } from "react";
import { GraduationCap, ExternalLink, ChevronDown, ChevronUp, CheckCircle2, Circle, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import BursaryEligibilityQuiz from "./BursaryEligibilityQuiz";
import { courseTuitionUsd } from "@/lib/courseTuition";
import { firstYearOnly } from "@/lib/feeYear";

const _COUNTRY_TO_ISO2 = {
  UK: "GB", "United Kingdom": "GB",
  US: "US", "United States": "US", USA: "US",
  Canada: "CA", Australia: "AU", Ireland: "IE",
  Germany: "DE", France: "FR", Netherlands: "NL",
  Belgium: "BE", Austria: "AT", Portugal: "PT",
  Spain: "ES", Italy: "IT", Sweden: "SE", Denmark: "DK",
  Norway: "NO", Finland: "FI", Switzerland: "CH",
  Singapore: "SG", UAE: "AE", India: "IN",
};

function fmtUsd(n) {
  if (n == null) return "—";
  return "$" + n.toLocaleString();
}

export default function MoneyLayerCard({ degree }) {
  // Rough total-course-cost estimate for the net-cost-after-scholarship
  // line. Tuition per year × duration + city living × 12 × duration.
  // Not a precise figure — we round hard so families read it as a
  // ballpark not a quote.
  const _tuitionPerYear = courseTuitionUsd(degree)
                       || courseTuitionUsd(degree, "tuition_domestic_annual_usd")
                       || 0;
  const _durationYrs = degree?.duration_years || 1;
  const _tuitionTotal = firstYearOnly(degree?.cost) ? null : _tuitionPerYear * _durationYrs;
  const level = (degree?.level || "").toLowerCase();
  const iso2  = _COUNTRY_TO_ISO2[degree?.country || ""] ||
                (degree?.id?.startsWith("uk-") ? "GB" :
                 degree?.id?.startsWith("us-") ? "US" :
                 degree?.id?.startsWith("ca-") ? "CA" :
                 degree?.id?.startsWith("au-") ? "AU" :
                 degree?.id?.startsWith("ie-") ? "IE" :
                 degree?.id?.startsWith("nl-") ? "NL" :
                 degree?.id?.startsWith("fr-") ? "FR" :
                 degree?.id?.startsWith("de-") ? "DE" :
                 degree?.id?.startsWith("be-") ? "BE" :
                 degree?.id?.startsWith("pt-") ? "PT" :
                 degree?.id?.startsWith("at-") ? "AT" : null);
  const { user } = useAuth();
  const [passports, setPassports] = useState([]);
  const [scholarships, setScholarships] = useState([]);
  const [showAllSch, setShowAllSch] = useState(false);
  // Feb 2026 · Task 3 — probe the bursary registry to decide whether
  // to render the card at all when scholarships list is empty. Keeps
  // the "silent when no data anywhere" posture from Wave 3.
  const [hasBursaries, setHasBursaries] = useState(false);

  // Pull the applicant's passports from the profile so the scholarship
  // match score can factor nationality in. Silent when signed-out —
  // the match chip on each card falls back to a "sign in to score
  // nationality" prompt in that state.
  useEffect(() => {
    if (!user) { setPassports([]); return; }
    api.get("/profile")
      .then((r) => setPassports(r.data?.passports || []))
      .catch(() => setPassports([]));
  }, [user]);

  useEffect(() => {
    if (!iso2) return;
    // Feb 2026: only fetch scholarships that match this course's level.
    // Previously we ALSO fetched postgraduate-only awards to show as a
    // "For your future Master's" block on UG pages — that surfaced
    // Rhodes / Commonwealth / Aga Khan awards on undergrad courses,
    // which read as confusing rather than helpful. Dropped.
    const paramsMain = new URLSearchParams({destination_country: iso2});
    if (level === "undergraduate" || level === "postgraduate") {
      paramsMain.set("level", level);
    }
    if (passports.length > 0) paramsMain.set("passports", passports.join(","));
    api.get(`/money/scholarships?${paramsMain.toString()}`)
      .then((r) => setScholarships(r.data?.scholarships || []))
      .catch(() => setScholarships([]));
  }, [iso2, level, passports]);

  // Probe bursary registry for this uni so the card mounts even when
  // no matching scholarships are on file. UK-only for now — the
  // needs-based UK bursary landscape is unique; we don't yet have
  // equivalents seeded for US/CA/AU flagships.
  // Feb 2026 · derive uni_id from degree.id prefix when the top-level
  // field is null (common — degree docs store uni_id only sparsely).
  const _uniId = degree?.uni_id || (degree?.id ? degree.id.split("__")[0] : null);
  useEffect(() => {
    if (!_uniId || iso2 !== "GB") { setHasBursaries(false); return; }
    api.get(`/money/bursaries?uni_id=${encodeURIComponent(_uniId)}`)
      .then((r) => setHasBursaries((r.data?.count || 0) > 0))
      .catch(() => setHasBursaries(false));
  }, [_uniId, iso2]);

  if (scholarships.length === 0 && !hasBursaries) return null;

  const visibleSchs = showAllSch ? scholarships : scholarships.slice(0, 3);

  return (
    <div
      data-testid="money-layer-card"
      className="rounded-xl border border-stone-200 bg-white p-5 space-y-6"
    >
      <div className="text-[10px] font-underground-sc tracking-[0.18em] text-stone-500">
        {scholarships.length > 0
          ? "Money Layer · scholarships & bursaries"
          : "Money Layer · bursaries"}
      </div>

      {/* Feb 2026 — the "Living in {city}" mini-table used to live here
          too, but it duplicated the "TOTAL COURSE + LIVING COST" card
          above with different currency and slightly different bucket
          definitions (mobile vs no-mobile utilities), which read as a
          data mismatch to users. The top card owns cost-of-living now;
          Money Layer owns scholarships. */}

      {/* Scholarships */}
      {scholarships.length > 0 && (
        <div data-testid="money-scholarships">
          <div className="flex items-center gap-2">
            <GraduationCap className="h-4 w-4 text-[#4C1D95]" aria-hidden />
            <h3 className="font-editorial text-xl text-[#002147]">
              Scholarships you may be eligible for
            </h3>
            <span className="text-[11px] font-mono text-stone-500">
              destination: {iso2}
            </span>
          </div>

          {/* Nudge signed-in users who haven't set a passport yet — the
              match score only lights up nationality when we know it.
              Compact inline picker unblocks the flow without a
              full-page profile detour. */}
          {user && passports.length === 0 && (
            <InlinePassportPicker
              onSaved={(next) => setPassports(next)}
            />
          )}
          {user && passports.length > 0 && (
            <div className="mt-2 text-[11px] text-stone-500">
              Scoring nationality for:{" "}
              <span className="font-mono text-stone-700">{passports.join(", ")}</span>{" "}
              <button
                type="button"
                onClick={() => setPassports([])}
                data-testid="money-scholarships-change-passport"
                className="text-[#4C1D95] hover:underline"
              >
                change
              </button>
            </div>
          )}
          <ul className="mt-3 space-y-2">
            {visibleSchs.map((s) => (
              <li
                key={s.id}
                data-testid={`money-scholarship-${s.id}`}
                className="rounded-lg border border-stone-200 bg-stone-50 p-3"
              >
                <div className="flex items-start gap-2 justify-between">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-editorial text-[15px] text-[#002147]">
                        {s.name}
                      </span>
                      {s.matched_count != null && (
                        <MatchChip
                          scholarship={s}
                          user={user}
                        />
                      )}
                      {s.competitive === "very" && (
                        <span className="text-[9px] font-underground-sc tracking-wider rounded-full border border-rose-300 bg-rose-50 text-rose-800 px-1.5 py-0.5">
                          Very competitive
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] font-mono text-stone-500 mt-0.5">
                      {s.funder}
                    </div>
                    <p className="mt-1 text-[13px] text-stone-700">{s.summary}</p>
                    <div className="mt-1 text-[12px] text-stone-600">
                      <span className="font-medium">{s.amount}</span>
                      {s.deadline_hint && (
                        <>
                          <span className="text-stone-400"> · </span>
                          <span>Deadline: {s.deadline_hint}</span>
                        </>
                      )}
                    </div>
                    {/* Net-cost line — only when we can compute one
                        AND we're at least 2/3 on match, so we don't
                        misleadingly promise savings to non-eligible
                        applicants. */}
                    {_tuitionTotal > 0 && s.covers_tuition && s.matched_count >= 2 && (
                      <div
                        data-testid={`money-net-cost-${s.id}`}
                        className="mt-1.5 inline-flex items-center gap-1.5 rounded-md border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-[11px] text-emerald-900"
                      >
                        <span className="font-mono line-through text-emerald-700/60">
                          {fmtUsd(_tuitionTotal)}
                        </span>
                        <span aria-hidden>→</span>
                        <span className="font-semibold">
                          {fmtUsd(s.covers_living ? 0 : Math.max(0, Math.round(_tuitionTotal * 0.15)))}
                        </span>
                        <span className="text-[10px] text-emerald-700/80">
                          after this scholarship
                        </span>
                      </div>
                    )}
                  </div>
                  <a
                    href={s.url}
                    target="_blank"
                    rel="noreferrer"
                    className="shrink-0 text-[#4C1D95] hover:underline inline-flex items-center gap-0.5 text-[12px] font-medium"
                    data-testid={`money-scholarship-link-${s.id}`}
                  >
                    Apply <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              </li>
            ))}
          </ul>
          {scholarships.length > 3 && (
            <button
              type="button"
              onClick={() => setShowAllSch((x) => !x)}
              data-testid="money-scholarships-toggle"
              className="mt-2 text-[12px] text-[#4C1D95] hover:underline inline-flex items-center gap-1"
            >
              {showAllSch ? (
                <><ChevronUp className="h-3 w-3" /> Show fewer</>
              ) : (
                <><ChevronDown className="h-3 w-3" /> Show all {scholarships.length}</>
              )}
            </button>
          )}
          <p className="mt-2 text-[11px] text-stone-500">
            Eligibility varies by nationality. Confirm exact criteria on each
            programme's official page before applying.
          </p>
        </div>
      )}

      {/* Feb 2026 — dropped the "For your future Master's" block that
          surfaced PG-only scholarships (Rhodes, Commonwealth, Aga Khan)
          on undergrad course pages. Per user feedback, forward-looking
          Masters awards read as confusing rather than helpful on a UG
          course. Scholarships block above is now level-filtered by the
          /api/money/scholarships `level` param. */}

      {/* Feb 2026 · Task 3 — Bursary Eligibility Quiz. Silent when
          the target uni has no curated bursary rows (component's own
          gate); UK-only per iso2 check. Positioned below the
          scholarships list so competitive/international awards still
          lead, and the needs-based auto-award quiz complements. */}
      {iso2 === "GB" && hasBursaries && (
        <BursaryEligibilityQuiz degree={degree} uni={{ id: _uniId }} />
      )}
    </div>
  );
}

// The 20 most common student passports across our applicant base.
// User can toggle several (dual nationality). Full list too long to
// fit inline — we surface a search box only if none of these fit.
const _PASSPORT_QUICKPICKS = [
  "GB","IN","US","CN","NG","AE","EG","PK","BD","LK","MY","SG",
  "KE","ZA","GH","BR","MX","AU","CA","DE",
];
const _COUNTRY_LABELS = {
  GB:"United Kingdom", IN:"India", US:"United States", CN:"China",
  NG:"Nigeria", AE:"UAE", EG:"Egypt", PK:"Pakistan", BD:"Bangladesh",
  LK:"Sri Lanka", MY:"Malaysia", SG:"Singapore", KE:"Kenya",
  ZA:"South Africa", GH:"Ghana", BR:"Brazil", MX:"Mexico",
  AU:"Australia", CA:"Canada", DE:"Germany",
};

function InlinePassportPicker({ onSaved }) {
  const [picked, setPicked] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const toggle = (iso) => {
    setPicked((cur) =>
      cur.includes(iso) ? cur.filter((x) => x !== iso) : [...cur, iso]
    );
  };

  const save = async () => {
    if (picked.length === 0) return;
    setSaving(true); setError(null);
    try {
      // Fetch existing profile first so we don't nuke other fields
      const { data: existing } = await api.get("/profile").catch(() => ({data: {}}));
      const payload = { ...existing, passports: picked };
      delete payload.email; delete payload.user_id;  // server ignores these anyway
      await api.post("/profile", payload);
      onSaved(picked);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not save — please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="money-inline-passport-picker"
      className="mt-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2.5 text-[12px] text-amber-900"
    >
      <div className="font-semibold">
        Tell us your passport(s) so we can score nationality eligibility
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {_PASSPORT_QUICKPICKS.map((iso) => (
          <button
            key={iso}
            type="button"
            onClick={() => toggle(iso)}
            data-testid={`money-passport-pick-${iso}`}
            className={
              "text-[11px] font-underground-sc tracking-wider rounded-full border px-2 py-0.5 transition " +
              (picked.includes(iso)
                ? "border-[#4C1D95] bg-[#4C1D95] text-white"
                : "border-stone-300 bg-white text-stone-700 hover:border-[#4C1D95]")
            }
          >
            {iso} · {_COUNTRY_LABELS[iso]}
          </button>
        ))}
      </div>
      <div className="mt-2 flex items-center gap-2">
        <button
          type="button"
          onClick={save}
          disabled={picked.length === 0 || saving}
          data-testid="money-passport-save"
          className="text-[11px] font-medium rounded-md bg-[#4C1D95] text-white px-3 py-1 disabled:opacity-50 hover:bg-[#5B21B6]"
        >
          {saving ? "Saving…" : `Save ${picked.length || ""} passport${picked.length === 1 ? "" : "s"}`}
        </button>
        <span className="text-[11px] text-stone-600">
          Or edit anytime from your{" "}
          <a href="/advanced" className="text-[#4C1D95] hover:underline">Advanced Search profile</a>.
        </span>
      </div>
      {error && (
        <div className="mt-1 text-[11px] text-rose-700">{error}</div>
      )}
    </div>
  );
}


function Stat({ label, value, highlight = false }) {  return (
    <div
      className={
        "rounded-md border p-2 " +
        (highlight
          ? "border-[#4C1D95] bg-[#F5F0FF]"
          : "border-stone-200 bg-stone-50")
      }
    >
      <div className="text-[10px] font-underground-sc tracking-wider text-stone-500">
        {label}
      </div>
      <div
        className={
          "mt-0.5 font-editorial text-base tabular-nums " +
          (highlight ? "text-[#4C1D95] font-semibold" : "text-[#002147]")
        }
      >
        {value}
      </div>
    </div>
  );
}


/**
 * MatchChip — "You match X of Y criteria" pill on each scholarship
 * card. Three criteria per scholarship: destination country match,
 * nationality match, and level match. Colour-coded green (perfect
 * fit), amber (partial), rose (mismatch). Signed-out users see a
 * "Sign in to score nationality" hint on hover.
 *
 * Uses the `matches`, `matched_count`, `total_count` fields the
 * backend attaches to every scholarship row.
 */
function MatchChip({ scholarship, user }) {
  const { matches, matched_count, total_count } = scholarship;
  // Style by fit strength:
  //   3/3 = green perfect · 2/3 = amber near-fit · 1/3 or 0 = rose slim
  //   unknown nationality (user not signed in) = neutral stone
  const isPerfect = total_count === 3 && matched_count === 3;
  const nationalityUnknown = matches?.nationality == null;
  const style = isPerfect
    ? "border-emerald-400 bg-emerald-50 text-emerald-800"
    : nationalityUnknown
      ? "border-stone-300 bg-stone-50 text-stone-700"
      : matched_count >= 2
        ? "border-amber-400 bg-amber-50 text-amber-900"
        : "border-rose-300 bg-rose-50 text-rose-800";

  const hint = nationalityUnknown
    ? "Add your passport to your profile so we can score nationality eligibility"
    : `${matches.destination ? "✓" : "✗"} destination · ${matches.nationality ? "✓" : "✗"} nationality · ${matches.level === null ? "?" : matches.level ? "✓" : "✗"} level`;

  return (
    <span
      data-testid={`money-scholarship-match-${scholarship.id}`}
      title={hint}
      className={
        "inline-flex items-center gap-1 text-[9px] font-underground-sc tracking-wider rounded-full border px-1.5 py-0.5 " +
        style
      }
    >
      {isPerfect ? (
        <CheckCircle2 className="h-2.5 w-2.5" aria-hidden />
      ) : nationalityUnknown ? (
        <Circle className="h-2.5 w-2.5" aria-hidden />
      ) : matched_count < 2 ? (
        <XCircle className="h-2.5 w-2.5" aria-hidden />
      ) : (
        <CheckCircle2 className="h-2.5 w-2.5" aria-hidden />
      )}
      You match {matched_count}/{total_count}
      {nationalityUnknown && !user && " · sign in"}
    </span>
  );
}
